// SAB-722: /withdrawals — "How withdrawals work".
//
// FACTUAL DOCUMENT. A card brand may hold us to every sentence here, so each
// claim traces to one of exactly two sources and nothing else:
//
//   (a) Soap (Tyler Carlin), verified 2026-08-19 — payout brands, processing
//       time, fees, and the fact that min/max are ours to set.
//   (b) Our own code, read directly:
//         supabase/functions/soap-create-withdrawal/index.ts  (RG -> KYC tier 2
//           -> geo, all fail-closed; withdrawable_balance() ceiling)
//         supabase/functions/soap-create-checkout/index.ts    (deposit $10 min /
//           $10,000 max)
//
// Deliberately NOT stated:
//   - Daily withdrawal limits — Tyler is still checking. Absent, not guessed.
//   - Aeropay / ACH-as-a-user-choice — that rail is hidden (SAB-720) and must
//     not be documented. The same-day-ACH mention below is Soap's internal
//     fallback rail, not a method the user picks.
//   - W-9 / 1099 thresholds — future SAB-548, slots in here when it lands.
//   - Any flat "Instant" claim. Soap's verified language is near-instant in the
//     common case with a same-day fallback, and the old UI copy said "Instant"
//     flat; that is corrected in NFLFundsSection in this same change.
import { Link } from 'react-router-dom';
import LegalPageShell from './LegalPageShell';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {children}
    </section>
  );
}

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm leading-relaxed text-muted-foreground">{children}</p>
);

const LI = ({ children }: { children: React.ReactNode }) => (
  <li className="text-sm leading-relaxed text-muted-foreground">{children}</li>
);

export default function WithdrawalsPage() {
  return (
    <LegalPageShell title="How Withdrawals Work">
      <div className="space-y-8">
        <P>
          This page explains how you withdraw money from your Sabotage Fantasy
          balance: who is eligible, which cards we can pay out to, how long it
          takes, what it costs, and the amounts involved. Withdrawals are
          processed by our payments provider, Soap.
        </P>

        <Section title="Before you can withdraw">
          <P>Every withdrawal is checked against all of the following. If any check does not pass, the withdrawal is declined.</P>
          <ul className="list-disc space-y-2 pl-5">
            <LI>
              <b className="text-foreground">Identity verification.</b> You must have
              completed identity verification and been approved. We cannot pay out to
              an unverified account.
            </LI>
            <LI>
              <b className="text-foreground">Location.</b> Your location is verified at
              the moment you withdraw, and you must be physically present in a state
              where Sabotage Fantasy operates. Being eligible when you deposited is not
              enough — the check runs again at withdrawal.
            </LI>
            <LI>
              <b className="text-foreground">Responsible gaming status.</b> If you are
              self-excluded or in a cool-off period, withdrawals are blocked for that
              period. Contact support if you need help.
            </LI>
            <LI>
              <b className="text-foreground">A payout account in your own name.</b> We
              pay out only to a verified account belonging to you. We do not send money
              to third parties.
            </LI>
          </ul>
        </Section>

        <Section title="Which cards we can pay out to">
          <P>
            Withdrawals are paid to a <b className="text-foreground">Visa or Mastercard
            debit card</b>. Those are the only two brands our payouts support.
          </P>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <P>
              <b className="text-foreground">American Express is not supported for
              withdrawals.</b> We accept American Express for deposits, but payouts
              cannot be sent to an Amex card. If you deposited with Amex, you will need
              a Visa or Mastercard debit card to withdraw. Card acceptance marks shown
              elsewhere on this site refer to the cards we accept for deposits.
            </P>
          </div>
        </Section>

        <Section title="How long it takes">
          <P>
            In most cases a withdrawal arrives{' '}
            <b className="text-foreground">within seconds to a few minutes</b>, where
            your debit card supports instant payouts or your bank supports real-time
            payments.
          </P>
          <P>
            If your card or bank does not support that, the payment falls back to a
            same-day bank transfer and can take{' '}
            <b className="text-foreground">up to one business day</b>. Which path
            applies depends on your card and your bank, not on anything you choose here.
          </P>
        </Section>

        <Section title="What it costs">
          <P>
            <b className="text-foreground">Withdrawals are currently free.</b> There is
            no withdrawal fee today, and the amount you request is the amount you
            receive. Our payment processor charges us a fee for each payout, and we
            cover it. If this changes, we will disclose any fee before you confirm a
            withdrawal.
          </P>
          <P>
            Your bank or card issuer is not involved in that fee and should not charge
            you for receiving the payment.
          </P>
        </Section>

        <Section title="How much you can withdraw">
          <ul className="list-disc space-y-2 pl-5">
            <LI>
              <b className="text-foreground">Minimum:</b> you need at least{' '}
              <b className="text-foreground">$10.00</b> of withdrawable balance to start
              a withdrawal.
            </LI>
            <LI>
              <b className="text-foreground">Maximum:</b> there is no maximum beyond your
              withdrawable balance.
            </LI>
            <LI>
              <b className="text-foreground">Your withdrawable balance</b> may be lower
              than your total balance. Funds from a deposit that has not finished
              settling, or money already committed to a withdrawal in progress, are not
              available to withdraw until they clear.
            </LI>
          </ul>
          <P>
            You choose the exact amount on the secure checkout screen, up to your
            withdrawable balance.
          </P>
        </Section>

        <Section title="Deposits, for reference">
          <P>
            Deposits are a minimum of <b className="text-foreground">$10.00</b> and a
            maximum of <b className="text-foreground">$10,000.00</b> per transaction.
            Deposits accept Visa, Mastercard, and American Express.
          </P>
        </Section>

        <Section title="Terms of Service">
          <P>
            This page describes how withdrawals work in practice. It does not replace
            our{' '}
            <Link
              to="/terms"
              className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              Terms of Service
            </Link>
            , which govern your account, eligibility, contests, and payments. Where
            this page and the Terms of Service differ, the Terms of Service control.
          </P>
          <P>
            See also our{' '}
            <Link
              to="/responsible-gaming"
              className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              Responsible Gaming
            </Link>{' '}
            page and{' '}
            <Link
              to="/state-availability"
              className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              State Availability
            </Link>
            .
          </P>
        </Section>

        <Section title="Questions">
          <P>
            If a withdrawal has not arrived in the time described above, or you are not
            sure which card to use, contact us at{' '}
            <a
              href="mailto:support@support.sabotagefantasy.com"
              className="font-medium text-primary underline underline-offset-4 hover:text-primary/80"
            >
              support@support.sabotagefantasy.com
            </a>
            .
          </P>
        </Section>
      </div>
    </LegalPageShell>
  );
}
